import { FC, useEffect, useMemo } from 'react'

import { getRtmListItems } from 'partner360/components/trainingCentre/components/trainingCentrePopup/components/trainingCentreContent/helpers/getRtmListItems'
import { getTopicListItems } from 'partner360/components/trainingCentre/components/trainingCentrePopup/components/trainingCentreContent/helpers/getTopicListItems'
import { TrainingCentreView } from 'partner360/components/trainingCentre/models'
import {
  ITrainingRtm,
  ITrainingSection,
  ITrainingTopic,
} from 'partner360/models/trainingCentre/ITrainingCentre'

import { TrainingCentreList } from './components/common/trainingCentreList/TrainingCentreList'
import { TrainingTopicContent } from './components/trainingTopicContent/TrainingTopicContent'

import styles from './TrainingCentreContent.module.scss'

interface ITrainingCentreContentProps {
  view: TrainingCentreView

  rtms: ITrainingRtm[]
  topics: ITrainingTopic[]
  sections: ITrainingSection[]

  selectedRtm: string | null
  selectedTopicId: string | null
  expandedSections: string[]
  searchValue: string

  onSelectRtm: (rtm: string) => void
  onSelectTopic: (topicId: string) => void
  onToggleSection: (sectionTitle: string) => void
}

export const TrainingCentreContent: FC<ITrainingCentreContentProps> = ({
  view,
  rtms,
  topics,
  sections,
  selectedRtm,
  selectedTopicId,
  expandedSections,
  onSelectRtm,
  onSelectTopic,
  onToggleSection,
  searchValue,
}) => {
  const normalizedSearchValue = searchValue.trim().toLowerCase()
  const isSearchActive = normalizedSearchValue.length >= 3

  const topicTitleSearchResults = useMemo(() => {
    if (!isSearchActive || view !== 'topics') {
      return topics
    }

    return topics.filter((topic) => {
      if (topic.RtmId !== selectedRtm) {
        return false
      }

      return topic.Title.toLowerCase().includes(normalizedSearchValue)
    })
  }, [isSearchActive, normalizedSearchValue, selectedRtm, topics, view])

  const topicBySectionSearchResult = useMemo(() => {
    if (!isSearchActive || view !== 'topics' || topicTitleSearchResults.length) {
      return null
    }

    return topics.find((topic) => {
      if (topic.RtmId !== selectedRtm) {
        return false
      }

      return sections.some((section) => {
        if (section.TopicId !== topic.Id) {
          return false
        }

        return (
          section.Title.toLowerCase().includes(normalizedSearchValue) ||
          section.Description.toLowerCase().includes(normalizedSearchValue)
        )
      })
    })
  }, [
    isSearchActive,
    normalizedSearchValue,
    sections,
    selectedRtm,
    topicTitleSearchResults.length,
    topics,
    view,
  ])

  useEffect(() => {
    if (topicBySectionSearchResult) {
      onSelectTopic(topicBySectionSearchResult.Id)
    }
  }, [onSelectTopic, topicBySectionSearchResult])

  const filteredSections = useMemo(() => {
    if (!isSearchActive || view !== 'topic') {
      return sections
    }

    return sections.filter((section) => {
      if (section.TopicId !== selectedTopicId) {
        return false
      }

      return (
        section.Title.toLowerCase().includes(normalizedSearchValue) ||
        section.Description.toLowerCase().includes(normalizedSearchValue)
      )
    })
  }, [isSearchActive, normalizedSearchValue, sections, selectedTopicId, view])

  return (
    <div className={styles.content}>
      {view === 'rtms' && <TrainingCentreList items={getRtmListItems(rtms, onSelectRtm)} />}

      {view === 'topics' && (
        <TrainingCentreList
          searchValue={searchValue}
          items={getTopicListItems(selectedRtm, topicTitleSearchResults, onSelectTopic)}
        />
      )}

      {view === 'topic' && (
        <TrainingTopicContent
          selectedTopicId={selectedTopicId}
          sections={filteredSections}
          expandedSections={expandedSections}
          onToggleSection={onToggleSection}
          searchValue={searchValue}
        />
      )}
    </div>
  )
}
